import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import axios from 'axios';
import {
  FocusedJourneyLayout, MaskContainer, MaskContext, MaskHeadline,
  MaskTextarea, PrimaryAction, SecondaryAction,
} from '@/components/mask/MaskSystem';
import { dateLocale } from '@/lib/dateLocale';

/* Phase 2B — Empfänger-Journey: E01 FÜR DICH → E02 GESEHEN→ANGEKOMMEN → E03 RESONANZ → E04 WEITERGEBEN → E05 BEWAHREN */

export default function SharedCarmaV2() {
  const { token } = useParams();
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [carma, setCarma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('e01');
  const [arrived, setArrived] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySent, setReplySent] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const [claimed, setClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const readStartRef = useRef(null);
  const endRef = useRef(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${API_V2}/carma/shared/${token}`);
        setCarma(res.data);
        if (res.data.claimed) setClaimed(true);
        if (res.data.replied) setReplySent(true);
      } catch (err) { setError(err.response?.data?.detail || t('v2.shared.notFound')); }
      finally { setLoading(false); }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, API_V2]);

  // Eingeloggt + bereits gelesen, aber noch nicht bewahrt → direkt zu E05
  useEffect(() => {
    if (user && carma?.read && !carma?.claimed && step === 'e01') setStep('e05');
  }, [user, carma, step]);

  const fireEvent = useCallback((type) => {
    axios.post(`${API_V2}/carma/shared/${token}/event`, { type }).catch(() => {});
  }, [API_V2, token]);

  const settle = useCallback(() => {
    setArrived((prev) => {
      if (!prev) fireEvent('read');
      return true;
    });
  }, [fireEvent]);

  const afterReadStep = carma?.canReply === false ? 'e04' : 'e03';

  // E02: adaptive Lesezeit (4–20s je nach Textlänge), früher bei Scroll ans Ende
  useEffect(() => {
    if (step !== 'e02' || arrived || !carma) return;
    readStartRef.current = Date.now();
    const secs = Math.min(20, Math.max(4, Math.round(carma.message.length / 16)));
    const timer = setTimeout(settle, secs * 1000);

    const scrollEl = document.querySelector('[data-testid="focused-journey"]');
    const onScroll = () => { scrolledRef.current = true; };
    scrollEl?.addEventListener('scroll', onScroll, { passive: true });

    let observer;
    if (endRef.current) {
      observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && scrolledRef.current) {
          const elapsed = Date.now() - readStartRef.current;
          if (elapsed >= 4000) settle();
        }
      }, { root: scrollEl, threshold: 0.9 });
      observer.observe(endRef.current);
    }
    return () => { clearTimeout(timer); scrollEl?.removeEventListener('scroll', onScroll); observer?.disconnect(); };
  }, [step, arrived, carma, settle]);

  const sendReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true); setReplyError(null);
    try {
      await axios.post(`${API_V2}/carma/shared/${token}/reply`, { message: replyText.trim() });
      setReplySent(true); setReplying(false);
    } catch (err) {
      setReplyError(err.response?.data?.detail || t('v2.common.error'));
    } finally { setSending(false); }
  };

  const passOn = () => {
    fireEvent('passed_on');
    if (user) navigate('/give');
    else navigate('/auth?redirect=/give');
  };

  const claim = async () => {
    if (claiming) return;
    setClaiming(true); setClaimError(null);
    try {
      await axios.post(`${API_V2}/carma/shared/${token}/claim`, {}, { withCredentials: true });
      setClaimed(true); setStep('kept');
    } catch (err) {
      setClaimError(err.response?.data?.detail || t('v2.common.error'));
    }
    finally { setClaiming(false); }
  };

  if (loading) {
    return (
      <FocusedJourneyLayout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-sm text-[var(--muted)]">{t('v2.common.loading')}</p>
        </div>
      </FocusedJourneyLayout>
    );
  }

  if (error && !carma) {
    return (
      <FocusedJourneyLayout>
        <MaskContainer maskKey="err" bgWord={t('v2.bgWords.carma')} align="center" bgShift="-8%">
          <MaskHeadline>{t('v2.shared.gone')}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mb-10">{error}</p>
          <SecondaryAction onClick={() => navigate('/')} data-testid="shared-error-home">{t('v2.common.whatIs')}</SecondaryAction>
        </MaskContainer>
      </FocusedJourneyLayout>
    );
  }

  const recipientFirst = (carma.recipientName || 'Du').split(' ')[0];
  const senderFirst = (carma.senderDisplay || carma.senderName || '').split(' ')[0];
  const dateStr = new Date(carma.createdAt).toLocaleDateString(dateLocale(i18n.language), { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <FocusedJourneyLayout>
      {/* E01 — FÜR DICH */}
      {step === 'e01' && (
        <MaskContainer maskKey="e01" bgWord={t('v2.bgWords.fuerDich')} align="center" bgShift="-10%" data-testid="receive-e01">
          <MaskContext>{t('v2.shared.wantsToSay', { name: carma.senderDisplay || carma.senderName })}</MaskContext>
          <MaskHeadline>{t('v2.shared.forYou', { name: recipientFirst })}</MaskHeadline>
          <PrimaryAction onClick={() => setStep('e02')} data-testid="receive-open-btn">{t('v2.shared.read')}</PrimaryAction>
        </MaskContainer>
      )}

      {/* E02 — GESEHEN → ANGEKOMMEN */}
      {step === 'e02' && (
        <div key="e02" className="relative min-h-screen flex flex-col" data-testid="receive-e02">
          <div
            className="mask-bg-word" aria-hidden="true" data-testid="bg-gesehen"
            style={arrived ? { animation: 'none', opacity: 0, transition: 'opacity 1800ms ease' } : { transition: 'opacity 1800ms ease' }}
          >
            <span style={{ transform: 'translateX(-8%)' }}>{t('v2.bgWords.gesehen')}</span>
          </div>
          <div
            className="mask-bg-word" aria-hidden="true" data-testid="bg-angekommen"
            style={{ animation: 'none', opacity: arrived ? 1 : 0, transition: 'opacity 1800ms ease' }}
          >
            <span style={{ transform: 'translateX(-8%)' }}>{t('v2.bgWords.angekommen')}</span>
          </div>
          <div className="relative z-10 flex-1 flex flex-col justify-center w-full max-w-xl mx-auto px-6 sm:px-8 py-24">
            <MaskContext>{t('v2.common.from', { name: carma.senderDisplay || carma.senderName })} · {dateStr}</MaskContext>
            <p className="mask-enter mask-enter-delay-1 mask-letter text-xl sm:text-2xl text-[var(--text)]" data-testid="receive-message">
              {carma.message}
            </p>
            {carma.context && <p className="mask-enter mask-enter-delay-2 text-sm text-[var(--muted)] italic mt-6">{carma.context}</p>}
            <div ref={endRef} aria-hidden="true" />
            <div className="mt-14 min-h-[88px]">
              {arrived && (
                <div className="mask-enter">
                  <p className="text-base text-[var(--muted)] leading-relaxed" data-testid="receive-arrived-text">
                    {t('v2.shared.arrivedText')}
                  </p>
                  <button
                    type="button"
                    onClick={() => setStep(afterReadStep)}
                    data-testid="receive-continue-btn"
                    className="mt-6 text-sm text-[var(--muted)] hover:text-[var(--text)] py-3 px-1 transition-colors underline underline-offset-4 decoration-[var(--muted)]/40"
                  >
                    {t('v2.common.continue')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* E03 — RESONANZ */}
      {step === 'e03' && (
        <MaskContainer maskKey="e03" bgWord={t('v2.bgWords.resonanz')} bgPosition="top" bgShift="12%" data-testid="receive-e03">
          {replySent ? (
            <>
              <MaskContext>{t('v2.common.for', { name: senderFirst })}</MaskContext>
              <MaskHeadline>{t('v2.shared.replyAt', { name: senderFirst })}</MaskHeadline>
              <div className="mt-4">
                <PrimaryAction onClick={() => setStep('e04')} data-testid="receive-after-reply-btn">{t('v2.common.continue')}</PrimaryAction>
              </div>
            </>
          ) : replying ? (
            <>
              <MaskContext>{t('v2.common.for', { name: senderFirst })}</MaskContext>
              <MaskHeadline>{t('v2.shared.replyHeadline')}</MaskHeadline>
              <MaskTextarea
                autoFocus
                value={replyText}
                onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                placeholder={t('v2.shared.replyPlaceholder')}
                style={{ minHeight: '24vh' }}
                data-testid="receive-reply-textarea"
              />
              {replyError && <p className="text-sm text-red-700/80 mt-4" data-testid="receive-reply-error">{replyError}</p>}
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
                <PrimaryAction onClick={sendReply} disabled={!replyText.trim() || sending} data-testid="receive-reply-send-btn">
                  {sending ? t('v2.common.loading') : t('v2.shared.reply')}
                </PrimaryAction>
                <SecondaryAction onClick={() => setReplying(false)} data-testid="receive-reply-cancel-btn">{t('v2.common.notNow')}</SecondaryAction>
              </div>
            </>
          ) : (
            <>
              <MaskHeadline>{t('v2.shared.giveBackQ', { name: senderFirst })}</MaskHeadline>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <PrimaryAction onClick={() => setReplying(true)} data-testid="receive-reply-btn">{t('v2.shared.reply')}</PrimaryAction>
                <SecondaryAction onClick={() => setStep('e04')} data-testid="receive-keep-quiet-btn">{t('v2.shared.keepQuiet')}</SecondaryAction>
              </div>
            </>
          )}
        </MaskContainer>
      )}

      {/* E04 — WEITERGEBEN */}
      {step === 'e04' && (
        <MaskContainer maskKey="e04" bgWord={t('v2.bgWords.weitergeben')} bgPosition="bottom" bgShift="-14%" data-testid="receive-e04">
          <MaskHeadline>{t('v2.shared.passOnHeadline')}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md">
            {t('v2.shared.passOnText')}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <PrimaryAction onClick={passOn} data-testid="receive-passon-btn">{t('v2.shared.tellSomeone')}</PrimaryAction>
            <SecondaryAction onClick={() => setStep('e05')} data-testid="receive-skip-passon-btn">{t('v2.shared.notNowSmall')}</SecondaryAction>
          </div>
        </MaskContainer>
      )}

      {/* E05 — BEWAHREN */}
      {step === 'e05' && (
        <MaskContainer maskKey="e05" bgWord={t('v2.bgWords.bewahren')} align="center" bgShift="-8%" data-testid="receive-e05">
          {claimed ? (
            <>
              <MaskHeadline>{t('v2.shared.alreadyYours')}</MaskHeadline>
              {user && <SecondaryAction onClick={() => navigate('/inbox')} data-testid="receive-to-inbox-btn">{t('v2.shared.toFuerdich')}</SecondaryAction>}
              {!user && <SecondaryAction onClick={() => navigate('/')} data-testid="receive-claimed-home-btn">{t('v2.common.whatIs')}</SecondaryAction>}
            </>
          ) : user ? (
            <>
              <MaskHeadline>{t('v2.keep.headline')}</MaskHeadline>
              <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md">
                {t('v2.shared.keepTextUser')}
              </p>
              {claimError && <p className="text-sm text-red-700/80 mb-4" data-testid="receive-claim-error">{claimError}</p>}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
                <PrimaryAction onClick={claim} disabled={claiming} data-testid="receive-claim-btn">
                  {claiming ? t('v2.common.loading') : t('v2.shared.keepBtn')}
                </PrimaryAction>
                <SecondaryAction onClick={() => setStep('end')} data-testid="receive-skip-claim-btn">{t('v2.common.maybeLater')}</SecondaryAction>
              </div>
            </>
          ) : (
            <>
              <MaskHeadline>{t('v2.keep.headline')}</MaskHeadline>
              <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md">
                {t('v2.shared.keepTextGuest')}
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
                <PrimaryAction onClick={() => navigate(`/auth?redirect=/carma/shared/${token}`)} data-testid="receive-register-btn">
                  {t('v2.shared.createPlace')}
                </PrimaryAction>
                <SecondaryAction onClick={() => setStep('end')} data-testid="receive-later-btn">{t('v2.common.maybeLater')}</SecondaryAction>
              </div>
            </>
          )}
        </MaskContainer>
      )}

      {/* KEPT — nach dem Bewahren */}
      {step === 'kept' && (
        <MaskContainer maskKey="kept" bgWord={t('v2.bgWords.bewahrt')} align="center" bgShift="-6%" data-testid="receive-kept">
          <MaskHeadline>{t('v2.experience.keptLabel')}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md">
            {t('v2.shared.keptText')}
          </p>
          <PrimaryAction onClick={() => navigate('/inbox')} data-testid="receive-kept-inbox-btn">{t('v2.shared.toFuerdich')}</PrimaryAction>
        </MaskContainer>
      )}

      {/* END — ruhiger Abschluss ohne Konto */}
      {step === 'end' && (
        <MaskContainer maskKey="end" bgWord={t('v2.bgWords.carma')} align="center" bgShift="-8%" data-testid="receive-end">
          <MaskHeadline>{t('v2.shared.endHeadline')}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md">
            {t('v2.shared.endText')}
          </p>
          <SecondaryAction onClick={() => navigate('/')} data-testid="receive-end-home-btn">{t('v2.common.whatIs')}</SecondaryAction>
        </MaskContainer>
      )}
    </FocusedJourneyLayout>
  );
}
